import { AuthenticationService } from "../authenticationService";
import { APIService } from "../apiService";
import {
  PaginatedShipments,
  ShipmentPaginationOptions,
  ShipmentRecord,
  PopulatedShipmentRecord,
  ShipmentInvoiceType
} from "src/types";
import { PostShipmentRequestBody } from "src/types/api/shipments/postShipment";
import { GetShipmentStatsResponse } from "src/types/api/shipments/getShipmentStats";
import { PatchShipmentRequestBody } from "src/types/api/shipments/patchShipment";
import { EnqueueShipmentJobResponse, ShipmentJobRecord } from "src/types/api/shipments/shipmentJob";

export interface CommercialInvoiceUrl {
  type: ShipmentInvoiceType;
  invoiceNumber: string;
  url: string;
}

export interface GetCommercialInvoiceUrlsResponse {
  invoices: CommercialInvoiceUrl[];
}

class ShipmentAPIService extends APIService {
  constructor(authenticationService: AuthenticationService) {
    super(authenticationService, "/v1/shipments");
  }

  async postShipment(requestBody: PostShipmentRequestBody): Promise<EnqueueShipmentJobResponse> {
    return await this.jwtRequest("POST", "", requestBody);
  }

  async getShipmentJob(jobId: string): Promise<ShipmentJobRecord> {
    return await this.jwtRequest("GET", `/jobs/${encodeURIComponent(jobId)}`);
  }

  async getShipments(queryParams?: ShipmentPaginationOptions): Promise<PaginatedShipments> {
    return await this.jwtRequest("GET", "", undefined, queryParams);
  }

  async getShipmentById(shipmentId: string): Promise<PopulatedShipmentRecord> {
    return await this.jwtRequest("GET", `/${encodeURIComponent(shipmentId)}`);
  }

  async getShipmentStats(): Promise<GetShipmentStatsResponse> {
    return await this.jwtRequest("GET", "/stats");
  }

  async getCommercialInvoiceUrls(shipmentId: string): Promise<GetCommercialInvoiceUrlsResponse> {
    return await this.jwtRequest("GET", `/${encodeURIComponent(shipmentId)}/commercial-invoice-url`);
  }

  async postBookParcelHero(shipmentId: string, body?: Record<string, unknown>): Promise<ShipmentRecord> {
    return await this.jwtRequest("POST", `/${encodeURIComponent(shipmentId)}/book-parcel-hero`, body);
  }

  async getParcelHeroDetails(shipmentId: string): Promise<ShipmentRecord> {
    return await this.jwtRequest("GET", `/${encodeURIComponent(shipmentId)}/parcel-hero-details`);
  }

  async postCancelParcelHero(shipmentId: string): Promise<ShipmentRecord> {
    return await this.jwtRequest("POST", `/${encodeURIComponent(shipmentId)}/cancel-parcel-hero`);
  }

  async postRerunParcelHero(shipmentId: string, body?: Record<string, unknown>): Promise<ShipmentRecord> {
    return await this.jwtRequest("POST", `/${encodeURIComponent(shipmentId)}/rerun-parcel-hero`, body);
  }

  async putRescheduleParcelHero(shipmentId: string, collectionDate: string): Promise<ShipmentRecord> {
    return await this.jwtRequest("PUT", `/${encodeURIComponent(shipmentId)}/reschedule-parcel-hero`, {
      collectionDate
    });
  }

  async patchShipment(shipmentId: string, requestBody: PatchShipmentRequestBody): Promise<EnqueueShipmentJobResponse> {
    return await this.jwtRequest("PATCH", `/${encodeURIComponent(shipmentId)}`, requestBody);
  }

  async postFinalizeShipment(shipmentId: string): Promise<ShipmentRecord> {
    return await this.jwtRequest("POST", `/${encodeURIComponent(shipmentId)}/finalize`);
  }
}

export { ShipmentAPIService };
